import React, { useMemo, useState } from 'react';
import { useAuth } from '@clerk/clerk-react';
import { colors } from '../theme';
import { modalStyles } from './modalStyles';
import { deviceCount } from '../utils/rmaDevices';

const API_BASE = process.env.REACT_APP_API_URL;

// Cases can only join a shipment while they are still on their way in or being
// worked on — mirrors the backend gate.
const BUNDLABLE = ['pending', 'assigned', 'received', 'awaiting_repair', 'repairing'];

const sameCustomer = (a, b) =>
  (a || '').trim().toLowerCase() === (b || '').trim().toLowerCase();

// Enough to tell two cases apart without opening them: who it belongs to and
// where it is in the flow, not just the number.
const label = rma => {
  const who = (rma.contactOrg || rma.customerName || rma.contactEmail || 'no customer').trim();
  const units = deviceCount(rma) > 1 ? ` · ${deviceCount(rma)} units` : '';
  return `${rma.rmaId} · ${rma.serialNumber}${units} · ${who} · ${rma.status}`;
};

// Bundle one existing case into another (issue #712). The case you pick as the
// keeper keeps its RMA number; the other case's units move across and it stops
// appearing on the board — its customer link keeps resolving.
export default function RmaBundleModal({ rmas, onClose, onBundled }) {
  const { getToken } = useAuth();
  const [targetId, setTargetId] = useState('');
  const [sourceId, setSourceId] = useState('');
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState('');

  const candidates = useMemo(
    () => (rmas || []).filter(r => BUNDLABLE.includes(r.status) && !r.mergedInto),
    [rmas]
  );

  const target = candidates.find(r => r._id === targetId) || null;

  // Only cases belonging to the same customer can share one RMA.
  const sources = useMemo(() => {
    if (!target) return [];
    // Same customer AND same stage — the units move together from here on, so
    // merging across stages would skip one unit's intake. Mirrors the backend.
    return candidates.filter(r =>
      r._id !== target._id &&
      r.status === target.status &&
      sameCustomer(r.contactEmail, target.contactEmail) &&
      sameCustomer(r.contactOrg, target.contactOrg)
    );
  }, [candidates, target]);

  const submit = async () => {
    if (!targetId || !sourceId) return;
    setSaving(true);
    setError('');
    try {
      const token = await getToken();
      const res = await fetch(`${API_BASE}/rma/${targetId}/bundle`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${token}` },
        body: JSON.stringify({ sourceId }),
      });
      const data = await res.json();
      if (!res.ok || !data.ok) {
        setError(data.message || 'Could not bundle those cases.');
        return;
      }
      onBundled(data.rma, data.mergedRmaId);
    } catch (err) {
      setError('Could not bundle those cases.');
    } finally {
      setSaving(false);
    }
  };

  return (
    <div style={modalStyles.overlay} onClick={onClose}>
      <div style={{ ...modalStyles.modal, maxWidth: 560 }} onClick={e => e.stopPropagation()}>
        <h2 style={{ marginBottom: '0.25rem' }}>Bundle cases</h2>
        <p style={{ margin: '0 0 1rem', fontSize: '0.85rem', color: colors.textMuted }}>
          Move the units from one case into another so they are handled and shipped together.
          The case you keep holds its RMA number.
        </p>

        <label style={fieldLabel}>Keep this case (its RMA number survives)</label>
        <select
          value={targetId}
          onChange={e => { setTargetId(e.target.value); setSourceId(''); setError(''); }}
          className="ni-select"
          style={{ width: '100%', marginBottom: '0.85rem' }}
        >
          <option value="">— choose a case —</option>
          {candidates.map(r => (
            <option key={r._id} value={r._id}>{label(r)}</option>
          ))}
        </select>

        <label style={fieldLabel}>Bundle this case into it</label>
        <select
          value={sourceId}
          onChange={e => { setSourceId(e.target.value); setError(''); }}
          className="ni-select"
          style={{ width: '100%', marginBottom: '0.5rem' }}
          disabled={!target}
        >
          <option value="">{target ? '— choose a case —' : 'choose the case to keep first'}</option>
          {sources.map(r => (
            <option key={r._id} value={r._id}>{label(r)}</option>
          ))}
        </select>

        {target && sources.length === 0 && (
          <p style={{ margin: '0 0 0.5rem', fontSize: '0.8rem', color: '#fbbf24' }}>
            No other case is at the <strong>{target.status}</strong> stage for{' '}
            {target.contactOrg || target.contactEmail || 'this customer'}. Cases can only be
            bundled with another case from the same customer at the same stage.
          </p>
        )}

        {candidates.length === 0 && (
          <p style={{ margin: '0 0 0.5rem', fontSize: '0.8rem', color: colors.textMuted }}>
            No cases on the board can be bundled right now — only cases that have not shipped yet are eligible.
          </p>
        )}

        {error && (
          <p style={{ margin: '0.4rem 0 0', fontSize: '0.85rem', color: '#f87171' }}>{error}</p>
        )}

        <div style={{ display: 'flex', justifyContent: 'space-between', marginTop: '1.25rem' }}>
          <button style={modalStyles.buttonAlt} onClick={onClose} disabled={saving}>Cancel</button>
          <button style={modalStyles.button} onClick={submit} disabled={saving || !targetId || !sourceId}>
            {saving ? 'Bundling…' : 'Bundle cases'}
          </button>
        </div>
      </div>
    </div>
  );
}

const fieldLabel = {
  display: 'block',
  fontSize: '0.72rem',
  fontWeight: 700,
  color: colors.textMuted,
  textTransform: 'uppercase',
  letterSpacing: '0.05em',
  marginBottom: '0.3rem',
};
